import { PmsAutoCompleteMeta, PmsReportMeta } from '../models/pms-report-meta';
import {
   ReportRiskSummary,
   ReportRiskSummaryItem
} from '../models/report-risk-summary';
import {
   ReportRiskData,
   ReportRiskDetail,
   ReportRowRiskMetadata
} from '../models/report-row-risk';

export type ReportJsonObject = Record<string, unknown>;

const ROW_RISK_PROPERTY_NAMES = [
   'HasRisk',
   'Highlight',
   'RiskCodes',
   'RiskSeverity',
   'IsFinancial',
   'IsOperations',
   'Risks'
] as const;

export function isRecord(value: unknown): value is ReportJsonObject {
   return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseReportJson(reportJson: unknown): ReportJsonObject {
   if (isRecord(reportJson)) {
      return reportJson;
   }

   if (typeof reportJson !== 'string') {
      return {};
   }

   try {
      const parsedReportJson: unknown = JSON.parse(reportJson);

      return isRecord(parsedReportJson) ? parsedReportJson : {};
   } catch {
      return {};
   }
}

export function getReportArray<T = ReportJsonObject>(
   reportJson: unknown,
   propertyName: string
): T[] {
   const parsedReportJson = parseReportJson(reportJson);
   const reportRows = parsedReportJson[propertyName];

   return Array.isArray(reportRows) ? (reportRows as T[]) : [];
}

export function getReportMeta(reportJson: unknown): PmsReportMeta | null {
   const parsedReportJson = parseReportJson(reportJson);
   const rawMeta = parsedReportJson['_Meta'];

   if (!isRecord(rawMeta)) {
      return null;
   }

   return {
      RiskCodes: readOptionalStringArray(rawMeta, 'RiskCodes'),
      RiskCount: readOptionalNumber(rawMeta, 'RiskCount'),
      AutoComplete: readAutoCompleteMeta(rawMeta),
      HighestSeverity: readOptionalNullableString(rawMeta, 'HighestSeverity'),
      TotalReportValue: readOptionalNumber(rawMeta, 'TotalReportValue'),
      FinancialRiskCount: readOptionalNumber(rawMeta, 'FinancialRiskCount'),
      OperationsRiskCount: readOptionalNumber(rawMeta, 'OperationsRiskCount'),
      RiskEvaluationStatus: readOptionalNullableString(
         rawMeta,
         'RiskEvaluationStatus'
      ),
      RiskEvaluationMessage: readOptionalNullableString(
         rawMeta,
         'RiskEvaluationMessage'
      ),
      RiskEvaluationVersion: readOptionalNullableString(
         rawMeta,
         'RiskEvaluationVersion'
      )
   };
}

export function getRiskSummary(reportJson: unknown): ReportRiskSummary | null {
   const parsedReportJson = parseReportJson(reportJson);
   const rawRiskSummary = parsedReportJson['RiskSummary'];

   if (!isRecord(rawRiskSummary)) {
      return null;
   }

   return {
      Items: readRiskSummaryItems(rawRiskSummary),
      RiskCount: readOptionalNumber(rawRiskSummary, 'RiskCount'),
      TotalRiskValue: readOptionalNullableNumber(rawRiskSummary, 'TotalRiskValue'),
      CombinedSummary: readOptionalNullableString(rawRiskSummary, 'CombinedSummary'),
      AffectedRowCount: readOptionalNumber(rawRiskSummary, 'AffectedRowCount'),
      CombinedRecommendedAction: readOptionalNullableString(
         rawRiskSummary,
         'CombinedRecommendedAction'
      )
   };
}

export function getReportRowRiskMetadata(row: unknown): ReportRowRiskMetadata | null {
   if (!isRecord(row) || !hasAnyRowRiskProperty(row)) {
      return null;
   }

   return {
      HasRisk: readOptionalBoolean(row, 'HasRisk'),
      Highlight: readOptionalBoolean(row, 'Highlight'),
      RiskCodes: readOptionalStringArray(row, 'RiskCodes'),
      RiskSeverity: readOptionalNullableString(row, 'RiskSeverity'),
      IsFinancial: readOptionalBoolean(row, 'IsFinancial'),
      IsOperations: readOptionalBoolean(row, 'IsOperations'),
      Risks: readRiskDetails(row)
   };
}

function readAutoCompleteMeta(
   rawMeta: ReportJsonObject
): PmsAutoCompleteMeta | null | undefined {
   if (!hasOwnProperty(rawMeta, 'AutoComplete')) {
      return undefined;
   }

   const rawAutoComplete = rawMeta['AutoComplete'];

   if (rawAutoComplete === null) {
      return null;
   }

   if (!isRecord(rawAutoComplete)) {
      return undefined;
   }

   return {
      Message: readOptionalNullableString(rawAutoComplete, 'Message'),
      Attempted: readOptionalBoolean(rawAutoComplete, 'Attempted'),
      Completed: readOptionalBoolean(rawAutoComplete, 'Completed'),
      ReasonCode: readOptionalNullableString(rawAutoComplete, 'ReasonCode')
   };
}

function readRiskSummaryItems(
   rawRiskSummary: ReportJsonObject
): ReportRiskSummaryItem[] | undefined {
   if (!hasOwnProperty(rawRiskSummary, 'Items')) {
      return undefined;
   }

   const rawItems = rawRiskSummary['Items'];

   if (!Array.isArray(rawItems)) {
      return undefined;
   }

   return rawItems
      .map((item) => parseRiskSummaryItem(item))
      .filter((item): item is ReportRiskSummaryItem => item !== null);
}

function parseRiskSummaryItem(value: unknown): ReportRiskSummaryItem | null {
   if (!isRecord(value)) {
      return null;
   }

   return {
      Name: readOptionalNullableString(value, 'Name'),
      Count: readOptionalNumber(value, 'Count'),
      Summary: readOptionalNullableString(value, 'Summary'),
      Category: readOptionalNullableString(value, 'Category'),
      RiskCode: readOptionalNullableString(value, 'RiskCode'),
      Severity: readOptionalNullableString(value, 'Severity'),
      RiskValue: readOptionalNullableNumber(value, 'RiskValue'),
      FindingCount: readOptionalNumber(value, 'FindingCount'),
      RecommendedAction: readOptionalNullableString(value, 'RecommendedAction')
   };
}

function readRiskDetails(row: ReportJsonObject): ReportRiskDetail[] | undefined {
   if (!hasOwnProperty(row, 'Risks')) {
      return undefined;
   }

   const rawRisks = row['Risks'];

   if (!Array.isArray(rawRisks)) {
      return undefined;
   }

   return rawRisks
      .map((risk) => parseRiskDetail(risk))
      .filter((risk): risk is ReportRiskDetail => risk !== null);
}

function parseRiskDetail(value: unknown): ReportRiskDetail | null {
   if (!isRecord(value)) {
      return null;
   }

   return {
      Message: readOptionalNullableString(value, 'Message'),
      Metadata: readOptionalNullableRecord(value, 'Metadata'),
      RiskCode: readOptionalNullableString(value, 'RiskCode'),
      Severity: readOptionalNullableString(value, 'Severity'),
      Highlight: readOptionalBoolean(value, 'Highlight'),
      Parameters: readOptionalNullableRecord(value, 'Parameters'),
      IsFinancial: readOptionalBoolean(value, 'IsFinancial'),
      IsOperations: readOptionalBoolean(value, 'IsOperations')
   };
}

function hasAnyRowRiskProperty(row: ReportJsonObject): boolean {
   return ROW_RISK_PROPERTY_NAMES.some((propertyName) =>
      hasOwnProperty(row, propertyName)
   );
}

function readOptionalStringArray(
   source: ReportJsonObject,
   propertyName: string
): string[] | undefined {
   if (!hasOwnProperty(source, propertyName)) {
      return undefined;
   }

   const value = source[propertyName];

   if (!Array.isArray(value)) {
      return undefined;
   }

   return value.filter((item): item is string => typeof item === 'string');
}

function readOptionalNullableString(
   source: ReportJsonObject,
   propertyName: string
): string | null | undefined {
   if (!hasOwnProperty(source, propertyName)) {
      return undefined;
   }

   const value = source[propertyName];

   if (value === null) {
      return null;
   }

   return typeof value === 'string' ? value : undefined;
}

function readOptionalNumber(
   source: ReportJsonObject,
   propertyName: string
): number | undefined {
   if (!hasOwnProperty(source, propertyName)) {
      return undefined;
   }

   const value = source[propertyName];

   return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function readOptionalNullableNumber(
   source: ReportJsonObject,
   propertyName: string
): number | null | undefined {
   if (!hasOwnProperty(source, propertyName)) {
      return undefined;
   }

   const value = source[propertyName];

   if (value === null) {
      return null;
   }

   return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function readOptionalBoolean(
   source: ReportJsonObject,
   propertyName: string
): boolean | undefined {
   if (!hasOwnProperty(source, propertyName)) {
      return undefined;
   }

   const value = source[propertyName];

   return typeof value === 'boolean' ? value : undefined;
}

function readOptionalNullableRecord(
   source: ReportJsonObject,
   propertyName: string
): ReportRiskData | null | undefined {
   if (!hasOwnProperty(source, propertyName)) {
      return undefined;
   }

   const value = source[propertyName];

   if (value === null) {
      return null;
   }

   return isRecord(value) ? value : undefined;
}

function hasOwnProperty(source: ReportJsonObject, propertyName: string): boolean {
   return Object.prototype.hasOwnProperty.call(source, propertyName);
}
